import { useEffect } from 'react';
import { useGoogleMap } from '@react-google-maps/api';
import { runWithDeadline } from '../utils/asyncRequest';
import { loadKmlLayer, projectKmlUrl } from '../utils/loadKmlLayer';

// KML is optional: a provider error must not unmount the dashboard.
export default function ProjectKmlLayers({ layers, projectId, fitBounds = false, onError, onLoading, onBounds }) {
    const map = useGoogleMap();

    useEffect(() => {
        const controller = new AbortController();
        const disposers = [];
        const warnings = new Set();
        const queue = Array.isArray(layers) ? layers.slice(0, 12) : [];
        const budget = { features: 0 };
        let active = true;
        let bounds = null;
        let pending = queue.length;
        onError(null);
        onLoading?.(Boolean(map && pending));
        const report = warning => {
            if (!active) return;
            warnings.add(warning);
            onError([...warnings].join(' '));
        };
        const loadNext = () => {
            if (!active || !map || queue.length === 0) return;
            const layer = queue.shift();
            runWithDeadline(async signal => {
                const dispose = await loadKmlLayer(map, projectKmlUrl(layer, projectId), { signal, fitBounds, budget, onWarning: report,
                    onBounds: value => {
                        if (!active) return;
                        bounds = bounds ? { west: Math.min(bounds.west, value.west), east: Math.max(bounds.east, value.east), south: Math.min(bounds.south, value.south), north: Math.max(bounds.north, value.north) } : value;
                        onBounds?.(bounds);
                    }
                });
                if (active) disposers.push(dispose); else dispose();
            }, { signal: controller.signal, timeoutMs: 30000 }).catch(error => {
                if (!active || error.name === 'AbortError') return;
                report(`KML 底圖：${error.message} 其他圖層與監測功能仍可使用。`);
            }).finally(() => {
                pending -= 1;
                if (active && pending === 0) onLoading?.(false);
                loadNext();
            });
        };
        if (layers?.length > 12) report('KML 圖層超過 12 個上限，已載入前 12 個；請拆分或簡化專案底圖。');
        // At most two parser workers/downloads per project at a time.
        loadNext();
        loadNext();

        return () => {
            active = false;
            controller.abort();
            disposers.forEach(dispose => dispose());
        };
    }, [map, layers, projectId, fitBounds, onError, onLoading, onBounds]);

    return null;
}
